import {
  FREE_MEMBERSHIP,
  MEMBERSHIP_ACCESS_CONFIG,
  MEMBERSHIP_GROUPS,
  MEMBERSHIP_ORDER,
  MEMBERSHIP_TYPES,
  PROFESSIONAL_MEMBERSHIP,
  STARTUP_MEMBERSHIP,
} from './config';

export function isKnownMembershipType(value) {
  return MEMBERSHIP_TYPES.includes(value);
}

export function normalizeMembershipType(value, fallback = FREE_MEMBERSHIP) {
  return isKnownMembershipType(value) ? value : fallback;
}

export function normalizeOptionalMembershipType(value) {
  return isKnownMembershipType(value) ? value : null;
}

export function isActiveMembershipUser(user) {
  return Boolean(user?.status === 'active');
}

export function resolveMembershipType(user, options = {}) {
  const { requireActive = true, fallback = FREE_MEMBERSHIP } = options;

  if (!user?.membershipType) {
    return fallback;
  }

  if (requireActive && !isActiveMembershipUser(user)) {
    return fallback;
  }

  return normalizeMembershipType(user.membershipType, fallback);
}

export function getMembershipTier(membershipType) {
  const normalized = normalizeMembershipType(membershipType);

  if (normalized === PROFESSIONAL_MEMBERSHIP) {
    return 'professional';
  }

  if (normalized === STARTUP_MEMBERSHIP) {
    return 'startup';
  }

  return 'free';
}

export function getOptionalMembershipTier(membershipType) {
  const normalized = normalizeOptionalMembershipType(membershipType);
  return normalized ? getMembershipTier(normalized) : null;
}

export function getCoInvestTier(membershipType) {
  const tier = getMembershipTier(membershipType);

  if (tier === 'professional') {
    return 'pro';
  }

  if (tier === 'startup') {
    return 'st';
  }

  return 'free';
}

export function isFreeMembershipType(membershipType) {
  return normalizeOptionalMembershipType(membershipType) === FREE_MEMBERSHIP;
}

export function isPaidMembershipType(membershipType) {
  const normalized = normalizeOptionalMembershipType(membershipType);
  return normalized
    ? hasMembershipAccess(normalized, MEMBERSHIP_GROUPS.paid)
    : false;
}

export function getPendingMembershipType(user) {
  return normalizeOptionalMembershipType(user?.pendingMembershipType);
}

export function getMembershipTranslationKey(membershipType) {
  const tier = getMembershipTier(membershipType);

  if (tier === 'professional') {
    return 'pagePricing.content.professionalMembership.name';
  }

  if (tier === 'startup') {
    return 'pagePricing.content.startupMembership.name';
  }

  return 'pagePricing.content.freeMembership.name';
}

export function hasPendingMembershipChange(user) {
  const pendingMembershipType = getPendingMembershipType(user);

  return Boolean(
    pendingMembershipType &&
      pendingMembershipType !== normalizeMembershipType(user?.membershipType)
  );
}

export function hasPendingDowngradeToFree(user) {
  return (
    getPendingMembershipType(user) === FREE_MEMBERSHIP &&
    getMembershipTier(user?.membershipType) !== 'free'
  );
}

export function hasOldPendingMembership(user) {
  const pendingMembershipType = getPendingMembershipType(user);

  return Boolean(
    pendingMembershipType &&
      pendingMembershipType !== normalizeMembershipType(user?.membershipType) &&
      pendingMembershipType !== FREE_MEMBERSHIP
  );
}

export function hasConflictingPendingMembership(user) {
  return (
    hasPendingDowngradeToFree(user) &&
    isActiveMembershipUser(user) &&
    Boolean(user?.paddleSubscriptionId)
  );
}

export function hasCancelledMembershipDowngrade(user) {
  return (
    user?.status === 'cancelled' &&
    hasPendingDowngradeToFree(user) &&
    Boolean(user?.endsAt)
  );
}

export function hasCancelledMembershipPendingPaid(user) {
  const pendingMembershipType = getPendingMembershipType(user);

  return (
    user?.status === 'cancelled' &&
    Boolean(pendingMembershipType) &&
    pendingMembershipType !== FREE_MEMBERSHIP &&
    Boolean(user?.pendingPriceId)
  );
}

export function canPreviewMembershipPlanChange(user) {
  if (!user?.paddleSubscriptionId) {
    return false;
  }

  return (
    (
      isPaidMembershipType(user?.membershipType) &&
      user?.status !== 'cancelled' &&
      user?.status !== 'scheduled_cancellation' &&
      !hasPendingDowngradeToFree(user) &&
      isActiveMembershipUser(user)
    ) ||
    hasPendingDowngradeToFree(user)
  );
}

export function isCurrentMembershipPlan(user, planKey, interval) {
  const currentPlanKey = getMembershipTier(user?.membershipType);

  if (planKey === 'free') {
    return currentPlanKey === 'free';
  }

  if (currentPlanKey === planKey && !user?.membershipPeriod) {
    return interval === 'month';
  }

  return currentPlanKey === planKey && user?.membershipPeriod === interval;
}

export function isPendingMembershipPlan(user, planKey, interval) {
  const pendingPlanKey = getOptionalMembershipTier(user?.pendingMembershipType);

  if (!pendingPlanKey || pendingPlanKey !== planKey) {
    return false;
  }

  if (planKey === 'free') {
    return true;
  }

  return user?.pendingMembershipPeriod === interval;
}

export function shouldCreateSubscriptionCheckout({
  paddleSubscriptionId,
  status,
  pendingMembershipType,
}) {
  return (
    !paddleSubscriptionId ||
    status === 'cancelled' ||
    status === 'scheduled_cancellation' ||
    isFreeMembershipType(pendingMembershipType)
  );
}

export function hasMembershipAccess(membershipType, allowedMemberships = MEMBERSHIP_GROUPS.all) {
  const normalizedMembership = normalizeMembershipType(membershipType);
  const safeAllowedMemberships =
    Array.isArray(allowedMemberships) && allowedMemberships.length
      ? allowedMemberships
      : MEMBERSHIP_GROUPS.all;

  return safeAllowedMemberships
    .map((value) => normalizeMembershipType(value))
    .includes(normalizedMembership);
}

export function getMembershipConfig(membershipType) {
  const normalizedMembership = normalizeMembershipType(membershipType);
  return (
    MEMBERSHIP_ACCESS_CONFIG[normalizedMembership] ||
    MEMBERSHIP_ACCESS_CONFIG[FREE_MEMBERSHIP]
  );
}

export function hasMembershipFeatureAccess(membershipType, featureKey) {
  return Boolean(getMembershipConfig(membershipType).features?.[featureKey]);
}

export function createMembershipAccess(membershipType, options = {}) {
  const {
    loading = false,
    isAuthenticated = false,
    isActive = false,
    user = null,
  } = options;

  const rawMembershipType = normalizeMembershipType(membershipType);
  const resolvedMembershipType = isActive ? rawMembershipType : FREE_MEMBERSHIP;
  const membershipConfig = getMembershipConfig(resolvedMembershipType);
  const membershipTier = getMembershipTier(resolvedMembershipType);
  const membershipRank = MEMBERSHIP_ORDER[resolvedMembershipType] || 0;
  const isFree = resolvedMembershipType === FREE_MEMBERSHIP;
  const isStartup = resolvedMembershipType === STARTUP_MEMBERSHIP;
  const isProfessional = resolvedMembershipType === PROFESSIONAL_MEMBERSHIP;
  const isPaid = hasMembershipAccess(resolvedMembershipType, MEMBERSHIP_GROUPS.paid);
  const pendingMembershipType = getPendingMembershipType(user);
  const currentPlanKey = getMembershipTier(rawMembershipType);
  const pendingPlanKey = getOptionalMembershipTier(pendingMembershipType);
  const hasPendingChange = hasPendingMembershipChange(user);
  const hasOldPendingMembershipState = hasOldPendingMembership(user);
  const hasPendingFreeDowngrade = hasPendingDowngradeToFree(user);
  const hasConflictingPendingChange = hasConflictingPendingMembership(user);
  const hasCancelledDowngradePending = hasCancelledMembershipDowngrade(user);
  const hasCancelledPaidPending = hasCancelledMembershipPendingPaid(user);
  const shouldCreateCheckoutSubscription = shouldCreateSubscriptionCheckout({
    paddleSubscriptionId: user?.paddleSubscriptionId,
    status: user?.status,
    pendingMembershipType,
  });

  const canAccessFeature = (featureKey) =>
    hasMembershipFeatureAccess(resolvedMembershipType, featureKey);

  const hasActiveMembershipAccess = (allowedMemberships) =>
    isActive && hasMembershipAccess(resolvedMembershipType, allowedMemberships);

  return {
    user,
    loading,
    isAuthenticated,
    isActive,
    rawMembershipType,
    membershipType: resolvedMembershipType,
    membershipTier,
    membershipRank,
    coInvestTier: getCoInvestTier(resolvedMembershipType),
    isFree,
    isStartup,
    isProfessional,
    isPaid,
    currentPlanKey,
    pendingMembershipType,
    pendingPlanKey,
    hasPendingChange,
    hasOldPendingMembership: hasOldPendingMembershipState,
    hasPendingFreeDowngrade,
    hasConflictingPendingChange,
    hasCancelledDowngradePending,
    hasCancelledPaidPending,
    shouldCreateCheckoutSubscription,
    features: membershipConfig.features,
    dealCard: membershipConfig.dealCard,
    canAccessFeature,
    canPreviewPlanChange: canPreviewMembershipPlanChange(user),
    hasMembershipAccess: (allowedMemberships) =>
      hasMembershipAccess(resolvedMembershipType, allowedMemberships),
    hasActiveMembershipAccess,
    isCurrentPlan: (planKey, interval) =>
      isCurrentMembershipPlan(user, planKey, interval),
    isPendingPlan: (planKey, interval) =>
      isPendingMembershipPlan(user, planKey, interval),
    canAccessBoards: canAccessFeature('boards'),
    canUseCRM: canAccessFeature('crm'),
    canAddInvestor: canAccessFeature('addInvestor'),
    canSeeSensitiveInvestorInfo: canAccessFeature('investorSensitiveInfo'),
    canUseInvestmentInfoFilter: canAccessFeature('investmentInfoFilter'),
    canUseDeals: canAccessFeature('deals'),
    canUseStatistics: canAccessFeature('statistics'),
    canUseAiFunding: canAccessFeature('aiFunding'),
    canUseDataExport: canAccessFeature('dataExport'),
    canSeeCoInvestDetails: canAccessFeature('coInvestDetails'),
    canViewDealRecipientCompany: canAccessFeature('dealRecipientCompany'),
  };
}

export function getMembershipAccessModel(user, options = {}) {
  const { loading = false } = options;

  return createMembershipAccess(user?.membershipType, {
    loading,
    isAuthenticated: Boolean(user),
    isActive: isActiveMembershipUser(user),
    user,
  });
}
